import { useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft } from '@/components/ui/icons';
import { catalogService } from '@/services';
import { useCartStore, useAuthStore, useSettingsStore } from '@/stores';
import { formatPrice } from '@/utils/format';
import type { Product } from '@/types';
import {
  Badge,
  Button,
  PriceBlock,
  ProductCard,
  ProductCarousel,
  ProductGallery,
  QuantityStepper,
} from '@/components/ui';

const PLACEHOLDER = 'https://via.placeholder.com/600x600?text=No+Image';
const PLACEHOLDER_SMALL = 'https://via.placeholder.com/200x200?text=No+Image';

export function ProductDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('common');
  const { isAuthenticated } = useAuthStore();
  const currency = useSettingsStore((s) => s.currency);
  const { addProduct, isLoading: isCartLoading } = useCartStore();
  const [addedMessage, setAddedMessage] = useState(false);
  const [cartError, setCartError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);

  const { data: product, isLoading, error } = useQuery({
    queryKey: ['product', id, currency],
    queryFn: () => catalogService.getProductById(id!, { currency }),
    enabled: !!id,
  });

  const categoryId = product?.categoryId ?? product?.category?.id;
  const { data: categoryProducts = [] } = useQuery({
    queryKey: ['products', 'category', categoryId, currency],
    queryFn: () => catalogService.getProductsByCategory(categoryId!, currency),
    enabled: !!categoryId,
  });

  const suggestedProducts = (categoryProducts as Product[])
    .filter((p) => p.id !== id)
    .slice(0, 10);

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!product) return;
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/products/${id}` } });
      return;
    }
    try {
      await addProduct(product.id, quantity);
      setAddedMessage(true);
      setTimeout(() => setAddedMessage(false), 2500);
    } catch (err) {
      console.error('Failed to add to cart:', err);
      setCartError(err instanceof Error ? err.message : 'Error al añadir al carrito');
      setTimeout(() => setCartError(null), 4000);
    }
  };

  if (isLoading || !id) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-lg text-brand-700">{t('common.loading')}</div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
        <p className="text-lg text-red-600">{t('common.productNotFound')}</p>
        <Button variant="outline" onClick={() => navigate('/products')}>
          {t('common.backToProducts')}
        </Button>
      </div>
    );
  }

  const isInStock = (product.stock ?? 0) > 0;
  const galleryImages =
    product.images && product.images.length > 0
      ? product.images.map((img) => ({ url: img.url, alt: product.name }))
      : [{ url: PLACEHOLDER, alt: product.name }];

  const carouselSlides: ReactNode[] = suggestedProducts.map((p) => {
    const imgUrl = p.images?.length ? p.images[0].url : PLACEHOLDER_SMALL;
    const inStock = (p.stock ?? 0) > 0;
    return (
      <ProductCard
        key={p.id}
        imageUrl={imgUrl}
        title={p.name}
        onClick={() => navigate(`/products/${p.id}`)}
        price={<PriceBlock amount={formatPrice(p.price, p.currency)} size="sm" />}
        badge={
          <Badge variant={inStock ? 'success' : 'danger'}>
            {inStock ? t('common.inStock') : t('common.outOfStock')}
          </Badge>
        }
      />
    );
  });

  const maxQty = product.stock != null && product.stock > 0 ? product.stock : undefined;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      {addedMessage ? (
        <div
          className="fixed right-4 top-4 z-50 rounded-lg bg-green-600 px-4 py-2 text-white shadow-lg"
          role="status"
        >
          {t('common.addedToCart')}
        </div>
      ) : null}
      {cartError ? (
        <div
          className="fixed right-4 top-4 z-50 rounded-lg bg-red-600 px-4 py-2 text-white shadow-lg"
          role="alert"
        >
          {cartError}
        </div>
      ) : null}

      <button
        type="button"
        onClick={() => navigate('/products')}
        className="mb-4 inline-flex items-center gap-1 font-sans text-sm font-medium text-brand-600 hover:text-brand-400 hover:underline"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden />
        {t('common.backToProducts')}
      </button>

      <div className="overflow-hidden rounded-lg border border-brand-700/15 bg-surface shadow-sm">
        <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2">
          <ProductGallery images={galleryImages} productName={product.name} />
          <div>
            <h1 className="mb-2 font-heading text-heading-xl text-brand-950">{product.name}</h1>
            {product.category ? (
              <p className="mb-4 text-sm text-brand-700">{product.category.name}</p>
            ) : null}
            <div className="mb-4">
              <PriceBlock
                amount={formatPrice(product.price, product.currency)}
                size="lg"
                secondary={
                  product.originalPrice != null && product.originalCurrency
                    ? `≈ ${formatPrice(product.originalPrice, product.originalCurrency)} (${t('common.sellerCurrency')})`
                    : undefined
                }
              />
            </div>
            <div className="mb-4">
              <Badge variant={isInStock ? 'success' : 'danger'}>
                {isInStock
                  ? `${t('common.inStock')} (${product.stock})`
                  : t('common.outOfStock')}
              </Badge>
            </div>
            {product.description ? (
              <p className="mb-6 whitespace-pre-wrap text-brand-950">{product.description}</p>
            ) : null}
            {isInStock ? (
              <div className="mb-4">
                <p className="mb-2 font-sans text-sm font-medium text-brand-950">
                  {t('common.quantity')}
                </p>
                <QuantityStepper
                  value={quantity}
                  onChange={setQuantity}
                  min={1}
                  max={maxQty}
                  decreaseLabel={t('aria.decreaseQuantity')}
                  increaseLabel={t('aria.increaseQuantity')}
                />
              </div>
            ) : null}
            <Button
              fullWidth
              size="lg"
              onClick={handleAddToCart}
              disabled={!isInStock || isCartLoading}
              loading={isCartLoading}
            >
              {isCartLoading ? t('common.addingToCart') : t('common.addToCart')}
            </Button>
          </div>
        </div>
      </div>

      {carouselSlides.length > 0 ? (
        <div className="mt-10">
          <ProductCarousel title={t('common.suggestedProducts')}>
            {carouselSlides}
          </ProductCarousel>
        </div>
      ) : null}
    </div>
  );
}
